import React, { useState, useEffect } from "react";
import ArrowDownIcon from "../../../icons/arrow-down.icon";
import { PlanData } from "./mock.data";
import styles from "./index.module.scss";
import { useTableSort, SortConfig } from "../../../../hooks/use-table-sort.hook";
import cn from "../../../../Utils/class-names.utils";
import SubscriptionsTableRow from "./subscriptions-table-row.component";

interface SubscriptionsTableProps {
  plansData: PlanData[];
  isView?: boolean;
  periodText?: string;
}

type SortField = keyof PlanData;

interface SortableHeaderCellProps {
  label: string;
  field: SortField;
  sortConfig: SortConfig<PlanData>;
  onSort: (field: SortField) => void;
}

const SortableHeaderCell = ({
  label,
  field,
  sortConfig,
  onSort,
}: SortableHeaderCellProps) => {
  const isActive = sortConfig.field === field && sortConfig.direction !== null;
  const direction = isActive ? sortConfig.direction : null;

  return (
    <div
      className={styles.headerCell}
      onClick={() => onSort(field)}
    >
      <div className={styles.headerContent}>
        <div className={styles.headerText}>{label}</div>
        {isActive && (
          <div
            className={cn(styles.arrowWrapper, {
              [styles.desc]: direction === "desc",
              [styles.asc]: direction === "asc",
            })}
          >
            <ArrowDownIcon />
          </div>
        )}
      </div>
    </div>
  );
};

const SubscriptionsTable = ({ plansData, isView = true, periodText = "/month" }: SubscriptionsTableProps) => {
  const { sortedData, sortConfig, handleSort } = useTableSort<PlanData>(plansData, {
    initialField: "name",
    initialDirection: "asc",
  });

  const [editedValues, setEditedValues] = useState<Record<string, { cost: number; price: number }>>({});

  useEffect(() => {
    const initialValues: Record<string, { cost: number; price: number }> = {};
    plansData.forEach((plan) => {
      const costNum = parseFloat(plan.cost.replace(/[^\d.]/g, "")) || 0;
      const priceNum = parseFloat(plan.price.replace(/[^\d.]/g, "")) || 0;
      initialValues[plan.name] = { cost: costNum, price: priceNum };
    });
    setEditedValues((prev) => {
      const merged = { ...initialValues };
      Object.keys(prev).forEach((planName) => {
        if (initialValues[planName]) {
          merged[planName] = prev[planName];
        }
      });
      return merged;
    });
  }, [plansData]);

  const handleCostChange = (planName: string, value: string | number) => {
    setEditedValues((prev) => ({
      ...prev,
      [planName]: { ...prev[planName], cost: typeof value === "string" ? parseFloat(value) || 0 : value },
    }));
  };

  const handlePriceChange = (planName: string, value: string | number) => {
    setEditedValues((prev) => ({
      ...prev,
      [planName]: { ...prev[planName], price: typeof value === "string" ? parseFloat(value) || 0 : value },
    }));
  };

  const handleCostIncrement = (planName: string) => {
    setEditedValues((prev) => ({
      ...prev,
      [planName]: { ...prev[planName], cost: (prev[planName]?.cost || 0) + 100 },
    }));
  };

  const handleCostDecrement = (planName: string) => {
    setEditedValues((prev) => ({
      ...prev,
      [planName]: { ...prev[planName], cost: Math.max(0, (prev[planName]?.cost || 0) - 100) },
    }));
  };

  const handlePriceIncrement = (planName: string) => {
    setEditedValues((prev) => ({
      ...prev,
      [planName]: { ...prev[planName], price: (prev[planName]?.price || 0) + 100 },
    }));
  };

  const handlePriceDecrement = (planName: string) => {
    setEditedValues((prev) => ({
      ...prev,
      [planName]: { ...prev[planName], price: Math.max(0, (prev[planName]?.price || 0) - 100) },
    }));
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <SortableHeaderCell
          label="Plan Name"
          field="name"
          sortConfig={sortConfig}
          onSort={handleSort}
        />
        <SortableHeaderCell
          label="Cost"
          field="cost"
          sortConfig={sortConfig}
          onSort={handleSort}
        />
        <SortableHeaderCell
          label="Price"
          field="price"
          sortConfig={sortConfig}
          onSort={handleSort}
        />
      </div>

      {sortedData.map((plan, index) => (
        <SubscriptionsTableRow
          key={`sub-${index}-${plan.name}`}
          plan={plan}
          index={index}
          isView={isView}
          editedValue={editedValues[plan.name]}
          periodText={periodText}
          onCostChange={(value) => handleCostChange(plan.name, value)}
          onCostIncrement={() => handleCostIncrement(plan.name)}
          onCostDecrement={() => handleCostDecrement(plan.name)}
          onPriceChange={(value) => handlePriceChange(plan.name, value)}
          onPriceIncrement={() => handlePriceIncrement(plan.name)}
          onPriceDecrement={() => handlePriceDecrement(plan.name)}
        />
      ))}
    </div>
  );
};

export default SubscriptionsTable;
